import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RichTextEditor } from "../RichTextEditor";
import {
  InternalControlData,
  FREQUENCY_OPTIONS,
  NATURE_OF_PROCEDURE_OPTIONS,
} from "../types";
import { Plus, Trash2 } from "lucide-react";
import { ControlOwnerSelectorDialog } from "./ControlOwnerSelectorDialog";
import { DependentControlSelectorDialog } from "./DependentControlSelectorDialog";
import { ControlDocumentManager } from "../ControlDocumentManager";

interface EvaluateDesignProps {
  data: InternalControlData;
  onChange: (data: Partial<InternalControlData>) => void;
  controlId?: string;
}

interface RommSummary {
  rommId: string;
  description: string;
  assertion: string[];
  assessment: string;
}

export const EvaluateDesign: React.FC<EvaluateDesignProps> = ({ data, onChange, controlId }) => {
  const [showControlOwnerDialog, setShowControlOwnerDialog] = useState(false);
  const [showDependentControlDialog, setShowDependentControlDialog] = useState(false);
  const [rommSummaries, setRommSummaries] = useState<RommSummary[]>([]);
  const [loadingRomms, setLoadingRomms] = useState(false);

  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Memoize control owners to prevent unnecessary re-renders
  const memoizedControlOwners = useMemo(() => data.controlOwners, [data.controlOwners]);
  const memoizedDependentControls = useMemo(
    () => data.dependentControls,
    [data.dependentControls]
  );

  const handleControlOwnersSelected = useCallback((owners: typeof data.controlOwners) => {
    onChangeRef.current({ controlOwners: owners });
  }, []);

  const handleCloseControlOwnerDialog = useCallback(() => {
    setShowControlOwnerDialog(false);
  }, []);

  const handleCloseDependentControlDialog = useCallback(() => {
    setShowDependentControlDialog(false);
  }, []);

  // Stabilize callbacks for ControlDocumentManager to prevent infinite loops
  const handleEvidenceDocumentsChange = useCallback((documents: typeof data.evidenceDesignDocuments) => {
    onChangeRef.current({ evidenceDesignDocuments: documents });
  }, []);

  const handleEvidenceTextResponseChange = useCallback((textResponse: typeof data.evidenceDesignTextResponse) => {
    onChangeRef.current({ evidenceDesignTextResponse: textResponse });
  }, []);

  const handleRemoveControlOwner = (ownerId: string) => {
    onChangeRef.current({
      controlOwners: data.controlOwners.filter((owner) => owner.id !== ownerId),
    });
  };

  const addDesignStep = () => {
    const newStep = {
      id: `di-step-${Date.now()}`,
      stepDescription: "",
      natureOfProcedure: "",
      procedureDescription: "",
    };
    onChangeRef.current({ designImplementationSteps: [...data.designImplementationSteps, newStep] });
  };

  const removeDesignStep = (id: string) => {
    onChangeRef.current({
      designImplementationSteps: data.designImplementationSteps.filter((s) => s.id !== id),
    });
  };

  const updateDesignStep = (id: string, field: string, value: string) => {
    onChangeRef.current({
      designImplementationSteps: data.designImplementationSteps.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    });
  };


  const handleDependentControlsSelected = useCallback(
    (selectedControls: typeof data.dependentControls) => {
      onChangeRef.current({ dependentControls: selectedControls });
      setShowDependentControlDialog(false);
    },
    []
  );

  const removeDependentControl = (id: string) => {
    onChangeRef.current({
      dependentControls: data.dependentControls.filter((c) => c.id !== id),
    });
  };

  // Load ROMM summaries for Design Factor 1
  useEffect(() => {
    const loadRommSummaries = async () => {
      if (!controlId) {
        setRommSummaries([]);
        return;
      }

      setLoadingRomms(true);
      try {
        // Step 1: Load Libraries_RCM.json to get romm array for this control
        let rommIds: string[] = [];
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function") {
          const rcmResult = await window.sharePointAPI.downloadRcmJson();
          if (rcmResult.success && rcmResult.data) {
            const librariesRcm = rcmResult.data.libraries_RCM || rcmResult.data.libraries_rcm || {};
            const controlData = librariesRcm[controlId];
            if (controlData && controlData.romm && Array.isArray(controlData.romm)) {
              rommIds = controlData.romm;
            }
          }
        }

        if (rommIds.length === 0) {
          setRommSummaries([]);
          setLoadingRomms(false);
          return;
        }

        // Step 2: Load Libraries_Romm.json to get ROMM details
        const summaries: RommSummary[] = [];
        if (window.sharePointAPI && typeof window.sharePointAPI.readRommLibrary === "function") {
          const rommResult = await window.sharePointAPI.readRommLibrary();
          if (rommResult.success && rommResult.data) {
            const rommLibrary = rommResult.data.romm_library || rommResult.data.rommLibrary || [];
            
            // Find each ROMM by ID
            for (const rommId of rommIds) {
              const romm = rommLibrary.find((r: any) => r["romm-id"] === rommId || r.rommId === rommId || r.romm_id === rommId);
              if (romm) {
                summaries.push({
                  rommId: romm["romm-id"] || romm.rommId || romm.romm_id || rommId,
                  description: romm.description || "",
                  assertion: Array.isArray(romm.assertion) ? romm.assertion : (romm.assertion ? [romm.assertion] : []),
                  assessment: romm.assesment || romm.assessment || "",
                });
              }
            }
          }
        }

        setRommSummaries(summaries);
      } catch (error) {
        console.error("Failed to load ROMM summaries:", error);
        setRommSummaries([]);
      } finally {
        setLoadingRomms(false);
      }
    };

    loadRommSummaries();
  }, [controlId]);

  // Helper function to get assessment icon
  const getAssessmentIcon = (assessment: string, rommId: string) => {
    const assessmentLower = assessment?.toLowerCase() || "";
    const uniqueId = rommId.replace(/[^a-zA-Z0-9]/g, '');
    
    if (assessmentLower === "lower") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <defs>
            <mask id={`lowerMask${uniqueId}`}>
              <rect width="16" height="16" fill="black"/>
              <polygon points="8,2 14,14 2,14" fill="white"/>
            </mask>
          </defs>
          <polygon points="8,2 14,14 2,14" fill="none" stroke="#CCCC00" strokeWidth="1"/>
          <rect x="0" y="10" width="16" height="6" fill="#CCCC00" mask={`url(#lowerMask${uniqueId})`}/>
        </svg>
      );
    } else if (assessmentLower === "higher") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <defs>
            <mask id={`higherMask${uniqueId}`}>
              <rect width="16" height="16" fill="black"/>
              <polygon points="8,2 14,14 2,14" fill="white"/>
            </mask>
          </defs>
          <polygon points="8,2 14,14 2,14" fill="none" stroke="#FFA500" strokeWidth="1"/>
          <rect x="0" y="8" width="16" height="8" fill="#FFA500" mask={`url(#higherMask${uniqueId})`}/>
        </svg>
      );
    } else if (assessmentLower === "significant") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <polygon points="8,2 14,14 2,14" fill="#FF0000"/>
        </svg>
      );
    } else if (assessmentLower === "nrpmm") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <polygon points="8,2 14,14 2,14" fill="none" stroke="#008000" strokeWidth="2"/>
        </svg>
      );
    }
    return null;
  };

  // Helper function to get assessment badge color
  const getAssessmentColor = (assessment: string) => {
    const assessmentLower = assessment?.toLowerCase() || "";
    switch (assessmentLower) {
      case 'higher':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'significant':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'lower':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'nrpmm':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  // Generate steps from Control Summary if detailed description is enabled
  const controlSteps = data.detailedDescriptionEnabled ? data.steps : [];

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">
          Evaluate Design and Determine Implementation
        </h2>
      </div>
      <div className="p-6 space-y-8">
        {/* Section 1: Design & Implementation */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white">1. Design & Implementation</h3>

          {/* Display control description first */}
          {data.controlDescription && (
            <div className="p-4 border border-white/10 rounded-lg bg-black/20">
              <Label className="text-sm font-medium text-white mb-2 block">
                Control Description
              </Label>
              <div
                className="text-white/60"
                dangerouslySetInnerHTML={{ __html: data.controlDescription }}
              />
            </div>
          )}

          {/* Steps from detailed description OR single procedure block */}
          {controlSteps.length > 0 ? (
            <div className="space-y-4">
              {controlSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4"
                >
                  <h4 className="text-sm font-semibold text-white">Step {index + 1}</h4>
                  <div
                    className="text-sm text-white/60 mb-4 p-3 bg-black/20 rounded-lg"
                    dangerouslySetInnerHTML={{ __html: step.content }}
                  />

                  <div className="space-y-4">
                    <h5 className="text-sm font-medium text-white">
                      Procedures performed to evaluate the design and implementation of control:
                    </h5>

                    <div className="space-y-2">
                      <Label className="text-white">Nature of testing procedure</Label>
                      <select
                        value={
                          data.designImplementationSteps.find((s) => s.id === `di-${step.id}`)
                            ?.natureOfProcedure || ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          const existingStep = data.designImplementationSteps.find(
                            (s) => s.id === `di-${step.id}`
                          );
                          if (existingStep) {
                            updateDesignStep(`di-${step.id}`, "natureOfProcedure", value);
                          } else {
                            onChange({
                              designImplementationSteps: [
                                ...data.designImplementationSteps,
                                {
                                  id: `di-${step.id}`,
                                  stepDescription: step.content,
                                  natureOfProcedure: value,
                                  procedureDescription: "",
                                },
                              ],
                            });
                          }
                        }}
                        className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                      >
                        <option value="">Select nature of procedure</option>
                        {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <RichTextEditor
                      label="Procedure Description"
                      value={
                        data.designImplementationSteps.find((s) => s.id === `di-${step.id}`)
                          ?.procedureDescription || ""
                      }
                      onChange={(content) => {
                        const existingStep = data.designImplementationSteps.find(
                          (s) => s.id === `di-${step.id}`
                        );
                        if (existingStep) {
                          updateDesignStep(`di-${step.id}`, "procedureDescription", content);
                        } else {
                          onChange({
                            designImplementationSteps: [
                              ...data.designImplementationSteps,
                              {
                                id: `di-${step.id}`,
                                stepDescription: step.content,
                                natureOfProcedure: "",
                                procedureDescription: content,
                              },
                            ],
                          });
                        }
                      }}
                      placeholder="Describe the procedure..."
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
              <h5 className="text-sm font-medium text-white">
                Procedures performed to evaluate the design and implementation of control:
              </h5>

              <div className="space-y-2">
                <Label className="text-white">Nature of testing procedure</Label>
                <select
                  value={data.designImplementationSteps[0]?.natureOfProcedure || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (data.designImplementationSteps.length > 0) {
                      updateDesignStep(data.designImplementationSteps[0].id, "natureOfProcedure", value);
                    } else {
                      onChange({
                        designImplementationSteps: [
                          {
                            id: `di-default-${Date.now()}`,
                            stepDescription: "",
                            natureOfProcedure: value,
                            procedureDescription: "",
                          },
                        ],
                      });
                    }
                  }}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                >
                  <option value="">Select nature of procedure</option>
                  {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <RichTextEditor
                label="Procedure Description"
                value={data.designImplementationSteps[0]?.procedureDescription || ""}
                onChange={(content) => {
                  if (data.designImplementationSteps.length > 0) {
                    updateDesignStep(
                      data.designImplementationSteps[0].id,
                      "procedureDescription",
                      content
                    );
                  } else {
                    onChange({
                      designImplementationSteps: [
                        {
                          id: `di-default-${Date.now()}`,
                          stepDescription: "",
                          natureOfProcedure: "",
                          procedureDescription: content,
                        },
                      ],
                    });
                  }
                }}
                placeholder="Describe the procedure..."
              />
            </div>
          )}

          {/* Test of design and implementation */}
          <RichTextEditor
            label="Test of design and implementation"
            value={data.testOfDesignImplementation}
            onChange={(content) => onChangeRef.current({ testOfDesignImplementation: content })}
            placeholder="Enter test of design and implementation..."
          />

          {/* Evidence of Design & Implementation */}
          <div className="space-y-3 border-t border-white/10 pt-6">
            <h4 className="text-sm font-semibold text-white">
              Evidence of Design & Implementation
            </h4>
            <p className="text-sm text-white/60">
              Add audit evidence obtained in evaluating design and determining implementation of
              control, if deemed appropriate.
            </p>
            <ControlDocumentManager
              controlId={data.controlId}
              workspaceLinked={data.workspaceLinked}
              documents={data.evidenceDesignDocuments || []}
              onDocumentsChange={handleEvidenceDocumentsChange}
              textResponse={data.evidenceDesignTextResponse}
              onTextResponseChange={handleEvidenceTextResponseChange}
              label="Evidence of Design"
            />
          </div>
        </div>

        {/* Section 2: Design Factors */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">2. Design Factors</h3>

          {/* Design Factor 1 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">
              Design Factor 1: Appropriateness of the purpose of control and its correlation to the
              risk/assertion identified and/or RAIT
            </h4>
            
            {loadingRomms ? (
              <div className="p-4 border border-white/10 rounded-lg bg-black/40 text-white/60 text-center">
                Loading associated RoMMs...
              </div>
            ) : rommSummaries.length === 0 ? (
              <div className="p-4 border border-white/10 rounded-lg bg-black/40 text-white/60 text-center">
                No RoMMs associated with this control.
              </div>
            ) : (
              <div className="space-y-4">
                {/* Display all ROMM summaries */}
                {rommSummaries.map((romm, index) => (
                  <div key={romm.rommId} className="p-4 border border-white/10 rounded-lg bg-black/40">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div>
                            <span className="text-xs font-medium text-white/60">RoMM ID:</span>
                            <span className="ml-2 text-sm font-semibold text-white">{romm.rommId}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-white/60">Description:</span>
                            <p className="mt-1 text-sm text-white">{romm.description}</p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-white/60">Assertion:</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {romm.assertion.map((assert, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-xs text-white"
                                >
                                  {assert}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Assessment in top right */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getAssessmentIcon(romm.assessment, romm.rommId)}
                          <span className={`text-xs px-2 py-1 rounded border ${getAssessmentColor(romm.assessment)}`}>
                            {romm.assessment}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Single Rich Text Editor for all ROMMs */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Label className="text-sm font-medium text-white mb-2 block">
                    Response:
                  </Label>
                  <RichTextEditor
                    value={data.designFactor1Response || ""}
                    onChange={(content) => onChange({ designFactor1Response: content })}
                    placeholder="Enter response for Design Factor 1..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Design Factor 2 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 2: Competence and authority of the person(s) performing the control
              </h4>
              <Switch
                checked={data.designFactor2Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor2Enabled: checked })}
              />
            </div>

            {data.designFactor2Enabled && (
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  className="border-white/10 text-white hover:bg-white/10"
                  onClick={() => setShowControlOwnerDialog(true)}
                >
                  + Associate Control Owner
                </Button>

                {data.controlOwners.length > 0 ? (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control Owner
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Job Title
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Background Information
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.controlOwners.map((owner) => (
                          <tr key={owner.id} className="border-t border-white/10">
                            <td className="px-4 py-3 text-white">{owner.name}</td>
                            <td className="px-4 py-3 text-white">{owner.jobTitle}</td>
                            <td className="px-4 py-3 text-white">{owner.background}</td>
                            <td className="px-4 py-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveControlOwner(owner.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 border border-white/10 rounded-lg bg-black/20 text-white/60 text-sm text-center">
                    No control owners associated. Click "+ Associate Control Owner" to add.
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Design Factor 3 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 3: Frequency and consistency with which the control is performed
              </h4>
              <Switch
                checked={data.designFactor3Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor3Enabled: checked })}
              />
            </div>

            {data.designFactor3Enabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Frequency</Label>
                  <select
                    value={data.frequency}
                    onChange={(e) => onChangeRef.current({ frequency: e.target.value })}
                    className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                  >
                    <option value="">Select frequency</option>
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <RichTextEditor
                  label="Consistency"
                  value={data.consistency}
                  onChange={(content) => onChangeRef.current({ consistency: content })}
                  placeholder="Describe consistency..."
                />
              </div>
            )}
          </div>

          {/* Design Factor 4 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 4: Level of aggregation and predictability
              </h4>
              <Switch
                checked={data.designFactor4Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor4Enabled: checked })}
              />
            </div>

            {data.designFactor4Enabled && (
              <RichTextEditor
                value={data.designFactor4Content}
                onChange={(content) => onChange({ designFactor4Content: content })}
                placeholder="Describe level of aggregation and predictability..."
              />
            )}
          </div>

          {/* Design Factor 5 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 5: Criteria for investigation (threshold) and process for follow-up
              </h4>
              <Switch
                checked={data.designFactor5Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor5Enabled: checked })}
              />
            </div>

            {data.designFactor5Enabled && (
              <RichTextEditor
                value={data.designFactor5Content}
                onChange={(content) => onChangeRef.current({ designFactor5Content: content })}
                placeholder="Describe criteria for investigation and follow-up process..."
              />
            )}
          </div>
        </div>

        {/* Section 3: Dependent Controls */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">3. Dependent Controls</h3>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="text-white">Is the control dependent on other controls?</Label>
              <select
                value={data.isDependentOnOtherControls ? "Yes" : "No"}
                onChange={(e) =>
                  onChange({ isDependentOnOtherControls: e.target.value === "Yes" })
                }
                className="w-32 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {data.isDependentOnOtherControls && (
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDependentControlDialog(true)} 
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  + Add
                </Button>

                {data.dependentControls.length > 0 && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Type of Control
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Nature
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-white/60">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.dependentControls.map((control) => (
                          <tr key={control.id} className="border-t border-white/10">
                            <td className="px-4 py-3 text-white">
                              {control.controlId || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.controlName || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.controlDescription || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.designImplementation || "-"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeDependentControl(control.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Section 4: Effectiveness of Design & Implementation */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">
            4. Effectiveness of Design & Implementation
          </h3>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">Is the design of this control effective?</Label>
              <select
                value={data.isDesignEffective || ""}
                onChange={(e) =>
                  onChangeRef.current({
                    isDesignEffective: e.target.value as "Yes" | "No" | "Not Yet Concluded",
                  })
                }
                className="w-48 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Yet Concluded">Not Yet Concluded</option>
              </select>
            </div>

            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">Is this control properly implemented?</Label>
              <select
                value={data.isProperlyImplemented || ""}
                onChange={(e) =>
                  onChangeRef.current({
                    isProperlyImplemented: e.target.value as "Yes" | "No" | "Not Yet Concluded",
                  })
                }
                className="w-48 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Yet Concluded">Not Yet Concluded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Dialogs - placed at bottom to avoid Radix setRef update loops */}
        <ControlOwnerSelectorDialog
          isOpen={showControlOwnerDialog}
          onClose={handleCloseControlOwnerDialog}
          onSelect={handleControlOwnersSelected}
          existingOwners={memoizedControlOwners}
        />
        <DependentControlSelectorDialog
          isOpen={showDependentControlDialog}
          onClose={handleCloseDependentControlDialog}
          onSelect={handleDependentControlsSelected}
          existingControls={memoizedDependentControls}
        />
      </div>
    </div>
  );
};

